// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
import { useMemo, useState } from 'react'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import useParticipationParameters from '../../hooks/useParticipationParameters'
import useMsiMonetaryTotal from '../../hooks/useMsiMonetaryTotal'
import useMaterialValue from '../../hooks/useMaterialValue'
import useMsiOnderbouwing from '../../hooks/useMsiOnderbouwing'
import buildSocietalValueBreakdown from '../../helpers/participationHelpers/buildSocietalValueBreakdown'
import { isPostsModel } from '../../helpers/participationHelpers/calculateParticipationImpact'
import useParticipationBirthDates from '../../hooks/useParticipationBirthDates'
import getComponentParameterBlocks from '../../helpers/participationHelpers/getComponentParameterBlocks'
import useOnderbouwingForEffect, { onderbouwingAvailable } from '../../hooks/useOnderbouwingForEffect'
import formatEuro from '../../helpers/formatEuro'
import formatEuroSigned from '../../helpers/formatEuroSigned'
import PageHeader from '../../components/layout/PageHeader'
import ParticipationSourcesModal from '../../components/dashboard/ParticipationSourcesModal'
import MsiOnderbouwingModal from '../../components/meetstandaard/MsiOnderbouwingModal'
import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'
import { client } from '../../hooks/Client'
import notAttributedText from '../../helpers/msiMonetisationHelpers/notAttributedText'

const MSI_COLUMNS = [
    { key: 'name', label: 'Effect' },
    { key: 'personas', label: 'Deelnemers' },
    { key: 'anonymous', label: 'Anoniem' },
    { key: 'remark', label: 'Toelichting' },
    { key: 'contribution', label: 'Bijdrage' },
    { key: 'attributed', label: 'Toegerekend' },
]

// Onder 2026.3 volgen de kolommen de onderdelen (posten) van de uitsplitsing.
const postsPersonaColumns = (components) => [
    { key: 'name', label: 'Deelnemer' },
    { key: 'change', label: 'Verandering' },
    ...components.map((component) => ({ key: component.key, label: component.label })),
    { key: 'total', label: 'Bijdrage' },
    { key: 'attributed', label: 'Toegerekend' },
]

const PERSONA_COLUMNS = [
    { key: 'name', label: 'Deelnemer' },
    { key: 'change', label: 'Verandering' },
    { key: 'incomePerson', label: 'Productiviteit' },
    { key: 'volunteerValue', label: 'Vrijwilligerswerk' },
    { key: 'benefitSavings', label: 'Besparing uitkering' },
    { key: 'publicCostSavings', label: 'Besparing uitvoering' },
    { key: 'total', label: 'Bijdrage' },
    { key: 'attributed', label: 'Toegerekend' },
]

// Rekenregels MSI: een deelnemer of effect zonder horizon krijgt geen €0 maar deze tekst.
// AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
const NOT_ATTRIBUTED = 'niet doorgerekend'

// Heeft dit MSI-effect minstens één deelnemer of de anonieme groep doorgerekend?
const msiAnyAttributed = (entry) =>
    (entry.perPersona || []).some((p) => p.attributed !== null && p.attributed !== undefined)
    || Boolean(entry.anonymous && entry.anonymous.attributed !== null && entry.anonymous.attributed !== undefined)

const msiAttributedCell = (entry) => {
    if (entry.excluded || entry.noScores) return '—'
    if (entry.notAttributedCount > 0 && !msiAnyAttributed(entry)) return NOT_ATTRIBUTED
    return formatEuroSigned(entry.attributedTotal)
}

const signClass = (value) => (value > 0 ? 'pos' : value < 0 ? 'neg' : 'neutral')

const formatStepChange = (change) => {
    if (change === null || change === undefined) return 'n.v.t.'
    if (change === 0) return '0 treden'
    const abs = Math.abs(change)
    const arrow = change > 0 ? '↑' : '↓'
    return `${arrow} ${abs} ${abs === 1 ? 'trede' : 'treden'}`
}

// Explains why an MSI effect contributes nothing (or not yet) to the total.
const msiRemark = (entry) => {
    if (entry.excluded && entry.excludedReason === 'samengevoegd') {
        return `Telt niet mee — waarde zit in ${entry.countedByName || 'een ander effect'}`
    }
    if (entry.excluded) return 'Telt niet mee — waarde zit in de participatieladder'
    if (entry.noScores) return 'Geen bedragen in de meetstandaard'
    if (entry.momentCount === 1 && entry.stateValue !== null) {
        return `Eén meetmoment — huidige situatiewaarde ${formatEuro(entry.stateValue)} (telt nog niet mee)`
    }
    return ''
}

/**
 * Detail page behind the dashboard "Maatschappelijke waarde" KPI card. Shows how
 * the live total is constructed from the two separate value streams: the
 * participatieladder (split by component and per deelnemer) and the MSI-effect
 * monetisation (split per effect, per deelnemer where coupled and anonymous as
 * a group). The streams are computed apart and only added here.
 */
const ParticipationValueDetail = () => {
    const { parameters, loading: parametersLoading, error: parametersError } = useParticipationParameters()
    const personas = useFirestoreGeneral('Personas', 'Status', 'active')
    const {
        total: msiTotal,
        attributedTotal: msiAttributedTotal,
        notAttributedCount: msiNotAttributedCount,
        notAttributedReasons: msiNotAttributedReasons,
        perEffect,
        loading: msiLoading,
        error: msiError,
    } = useMsiMonetaryTotal()
    // Standaard duur en uren van de organisatie (rekenregels MSI); '' zolang de read loopt.
    const compagnyMeta = useFirestoreGeneral('CompagnyMeta', 'CompagnyID', client)
    const doseLoaded = Array.isArray(compagnyMeta)
    const orgDefault = doseLoaded ? (compagnyMeta[0]?.DefaultInterventionDose || null) : null
    const { value: materialValue, unknownCount: materialUnknownCount, error: materialError } = useMaterialValue()
    const [activeComponent, setActiveComponent] = useState(/** @type {{key: string, label: string} | null} */ (null))
    // Geboortedatums voor de leeftijdsovergang (alleen onder 2026.3).
    const { birthDates, error: birthDatesError } = useParticipationBirthDates(isPostsModel(parameters))

    // Onderbouwing behind the MSI amounts (meetstandaard monetarisering-
    // document). Fails visibly but never blocks the euro display: without an
    // entry the effect name is plain text instead of a trigger.
    const { index: onderbouwingIndex, meta: onderbouwingMeta } = useMsiOnderbouwing()
    // A breakdown entry: it carries the scores the effect was valued at, in its
    // own version — looking them up again by MSIId alone could show another version's.
    const [activeMsiEntry, setActiveMsiEntry] = useState(/** @type {{msiId: string, name: string, scores?: Array<object>} | null} */ (null))

    // The excluded Participatieladder keeps its own onderbouwing (the
    // participatieladder-parameter modals) — no MSI trigger there. A versioned
    // effect is explained from the document it is valued with, which only
    // makes sense while it has amounts (`scores`).
    const effectRef = (entry) => ({
        msiId: entry.msiId,
        name: entry.name,
        standaardVersion: entry.scores ? entry.standaardVersion : null,
    })
    const hasOnderbouwing = (entry) => !entry.excluded && onderbouwingAvailable(onderbouwingIndex, effectRef(entry))
    const onderbouwing = useOnderbouwingForEffect(activeMsiEntry ? effectRef(activeMsiEntry) : null, onderbouwingIndex, onderbouwingMeta)

    const breakdown = useMemo(
        () => (parameters && birthDates && doseLoaded
            ? buildSocietalValueBreakdown(Array.isArray(personas) ? personas : [], parameters, birthDates, orgDefault)
            : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [personas, parameters, birthDates, doseLoaded, JSON.stringify(orgDefault)],
    )

    const loading = parametersLoading || msiLoading || !doseLoaded || (Boolean(parameters) && birthDates === null && !birthDatesError)
    const error = parametersError || msiError || materialError || birthDatesError
    // Alle drie de stromen moeten er zijn — een deeltotaal doet zich
    // stilzwijgend als het geheel voor.
    const combinedTotal = breakdown && msiTotal !== null && materialValue !== null
        ? breakdown.total + msiTotal + materialValue
        : null
    // Toegerekend aan de interventie: ladder + MSI-effecten (materialen hebben
    // hun eigen looptijd en vallen hier buiten). Wie niet doorgerekend kon
    // worden, telt niet als 0 maar wordt genoemd.
    const attributedTotal = breakdown && msiTotal !== null ? breakdown.attributedTotal + msiAttributedTotal : null
    const notAttributedCount = breakdown && msiTotal !== null ? breakdown.notAttributedCount + msiNotAttributedCount : 0
    const notAttributedReasons = { ...(msiNotAttributedReasons || {}) }
    Object.entries(breakdown?.notAttributedReasons || {}).forEach(([reason, n]) => {
        notAttributedReasons[reason] = (notAttributedReasons[reason] || 0) + n
    })
    const anyAttributed = Boolean(breakdown) && (
        breakdown.personas.some((p) => p.attributed !== null && p.attributed !== undefined)
        || breakdown.attributedTotal !== 0
        || (perEffect || []).some((entry) => !entry.excluded && !entry.noScores && msiAnyAttributed(entry))
    )

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading='Maatschappelijke waarde'
                    description='Hier zie je hoe het totaal aan maatschappelijke waarde is opgebouwd: per onderdeel en per deelnemer. Het totaal is de optelsom van het verschil tussen de beginsituatie en de huidige situatie van elke deelnemer.'
                    AI={false}
                />

                {loading && <p className='participation-detail-status'>Rekenparameters laden…</p>}

                {!loading && (error || !parameters) && (
                    <p className='participation-detail-error'>
                        De rekenwaarden konden niet worden geladen. Vernieuw de pagina of probeer het later opnieuw.
                    </p>
                )}

                {!loading && !error && breakdown && combinedTotal !== null && (
                    <div className='participation-detail'>
                        <div className='participation-detail-hero'>
                            <span className='participation-detail-hero-label'>Totale maatschappelijke waarde</span>
                            <span className='participation-detail-hero-value'>{formatEuro(combinedTotal)}</span>
                            <div className='participation-detail-streams'>
                                <div className='participation-detail-stream-row'>
                                    <span className='lbl'>Participatieladder</span>
                                    <span className='val'>{formatEuro(breakdown.total)}</span>
                                </div>
                                <div className='participation-detail-stream-row'>
                                    <span className='lbl'>MSI-effecten</span>
                                    <span className='val'>{formatEuro(msiTotal)}</span>
                                </div>
                                <div className='participation-detail-stream-row'>
                                    <span className='lbl'>
                                        Materialen en coachadviezen
                                        {materialUnknownCount > 0 && (
                                            <span className='participation-detail-component-note'>
                                                {materialUnknownCount === 1
                                                    ? '1 registratie kon niet worden doorgerekend'
                                                    : `${materialUnknownCount} registraties konden niet worden doorgerekend`}
                                            </span>
                                        )}
                                    </span>
                                    <span className='val'>{formatEuro(materialValue)}</span>
                                </div>
                            </div>
                            {attributedTotal !== null && (anyAttributed || notAttributedCount > 0) && (
                                <div className='participation-detail-streams'>
                                    <div className='participation-detail-stream-row'>
                                        <span className='lbl'>
                                            Toegerekend aan de interventie
                                            {notAttributedCount > 0 && (
                                                <span className='participation-detail-component-note'>
                                                    {notAttributedText(anyAttributed ? notAttributedCount : null, notAttributedReasons)}
                                                </span>
                                            )}
                                        </span>
                                        <span className='val'>{anyAttributed ? formatEuro(attributedTotal) : '—'}</span>
                                    </div>
                                </div>
                            )}
                            <span className='participation-detail-hero-meta'>
                                Op basis van {breakdown.personas.length}{' '}
                                {breakdown.personas.length === 1 ? 'deelnemer' : 'deelnemers'}
                            </span>
                        </div>

                        <div className='participation-detail-section'>
                            <h2>Participatieladder — per onderdeel</h2>
                            <p className='participation-detail-section-sub'>
                                Waar de participatieladder-waarde vandaan komt, opgeteld over alle deelnemers.
                            </p>
                            <div className='participation-detail-components'>
                                {breakdown.components.map((component) => (
                                    <div key={component.key} className='participation-detail-component-row'>
                                        <button
                                            type='button'
                                            className='lbl participation-detail-source-trigger'
                                            onClick={() => setActiveComponent(component)}
                                        >
                                            {component.label}
                                        </button>
                                        <span className={`val ${signClass(component.value)}`}>
                                            {formatEuroSigned(component.value)}
                                        </span>
                                    </div>
                                ))}
                                <div className='participation-detail-component-row participation-detail-component-total'>
                                    <span className='lbl'>Subtotaal participatieladder</span>
                                    <span className='val'>{formatEuro(breakdown.total)}</span>
                                </div>
                            </div>
                            {breakdown.model === 'posts' && (
                                <div className='participation-detail-components participation-detail-per-type'>
                                    <p className='participation-detail-section-sub'>Per soort waarde</p>
                                    {breakdown.perType.map((row) => (
                                        <div key={row.key} className='participation-detail-component-row'>
                                            <span className='lbl'>{row.label}</span>
                                            <span className={`val ${signClass(row.value)}`}>{formatEuroSigned(row.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='participation-detail-section'>
                            <h2>Participatieladder — per deelnemer</h2>
                            <p className='participation-detail-section-sub'>
                                Wat elke deelnemer bijdraagt aan de participatieladder-waarde.
                            </p>
                            <Table
                                columns={breakdown.model === 'posts' ? postsPersonaColumns(breakdown.components) : PERSONA_COLUMNS}
                                items={breakdown.personas}
                                getItemKey={(persona) => persona.id}
                                showSearch={false}
                                emptyMessage='Nog geen deelnemers met een ingevulde huidige situatie. Vul de participatieladder in bij een persona om waarde te zien.'
                                renderRow={(persona) => breakdown.model === 'posts' ? (
                                    <>
                                        <TableCell>{persona.name}</TableCell>
                                        <TableCell>{formatStepChange(persona.ladderStepChange)}</TableCell>
                                        {breakdown.components.map((component) => (
                                            <TableCell key={component.key} className={signClass(persona.values[component.key])}>
                                                {formatEuroSigned(persona.values[component.key])}
                                            </TableCell>
                                        ))}
                                        <TableCell className='participation-detail-table-total'>{formatEuro(persona.total)}</TableCell>
                                        <TableCell>{persona.attributed === null ? NOT_ATTRIBUTED : formatEuroSigned(persona.attributed)}</TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell>{persona.name}</TableCell>
                                        <TableCell>{formatStepChange(persona.ladderStepChange)}</TableCell>
                                        <TableCell className={signClass(persona.incomePerson)}>{formatEuroSigned(persona.incomePerson)}</TableCell>
                                        <TableCell className={signClass(persona.volunteerValue)}>{formatEuroSigned(persona.volunteerValue)}</TableCell>
                                        <TableCell className={signClass(persona.benefitSavings)}>{formatEuroSigned(persona.benefitSavings)}</TableCell>
                                        <TableCell className={signClass(persona.publicCostSavings)}>{formatEuroSigned(persona.publicCostSavings)}</TableCell>
                                        <TableCell className='participation-detail-table-total'>{formatEuro(persona.total)}</TableCell>
                                        <TableCell>{persona.attributed === null ? NOT_ATTRIBUTED : formatEuroSigned(persona.attributed)}</TableCell>
                                    </>
                                )}
                            />
                        </div>

                        <div className='participation-detail-section'>
                            <h2>MSI-effecten — per effect</h2>
                            <p className='participation-detail-section-sub'>
                                Waardeverandering per meetstandaard-effect tussen het eerste en laatste
                                meetmoment: per deelnemer waar een respons aan een deelnemer is gekoppeld,
                                en als groep voor anonieme responsen.
                            </p>
                            <Table
                                columns={MSI_COLUMNS}
                                items={perEffect}
                                getItemKey={(entry) => entry.effectId || entry.msiId}
                                showSearch={false}
                                emptyMessage='Nog geen MSI-effecten. Voeg effecten uit de meetstandaard toe om hier waarde te zien.'
                                renderRow={(entry) => (
                                    <>
                                        <TableCell>
                                            {hasOnderbouwing(entry) ? (
                                                <button
                                                    type='button'
                                                    className='participation-detail-source-trigger'
                                                    onClick={() => setActiveMsiEntry(entry)}
                                                >
                                                    {entry.name}
                                                </button>
                                            ) : entry.name}
                                        </TableCell>
                                        <TableCell>{entry.perPersona.length}</TableCell>
                                        <TableCell>{entry.anonymous ? entry.anonymous.respondentCount : 0}</TableCell>
                                        <TableCell className='participation-detail-msi-remark'>{msiRemark(entry)}</TableCell>
                                        <TableCell className={`participation-detail-table-total ${signClass(entry.deltaTotal)}`}>
                                            {entry.excluded || entry.noScores ? '—' : formatEuroSigned(entry.deltaTotal)}
                                        </TableCell>
                                        <TableCell>{msiAttributedCell(entry)}</TableCell>
                                    </>
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>

            <ParticipationSourcesModal
                componentLabel={activeComponent?.label || null}
                blocks={activeComponent ? getComponentParameterBlocks(activeComponent.key, parameters) : []}
                onClose={() => setActiveComponent(null)}
            />

            <MsiOnderbouwingModal
                entry={onderbouwing.entry}
                meta={onderbouwing.meta}
                loading={onderbouwing.loading}
                error={onderbouwing.error}
                effectName={onderbouwing.effectName}
                msiScores={activeMsiEntry?.scores || null}
                onClose={() => setActiveMsiEntry(null)}
            />
        </div>
    )
}

export default ParticipationValueDetail
